import { z } from 'zod'

import { getPaginationDataSchema } from './common.schema'

export const notificationSchema = z.object({
  id: z.number(),
  type: z.string(),
  channels: z.string(),
  metadataJson: z.string(),
  createdAt: z.string(),
  readAt: z.string().nullable(),
})

export type TNotification = z.infer<typeof notificationSchema>

export const notificationListSchema = z.array(notificationSchema)

export type TNotificationList = z.infer<typeof notificationListSchema>

export const notificationPaginationResponseSchema = getPaginationDataSchema(notificationSchema)

export type TNotificationPaginationResponse = z.infer<typeof notificationPaginationResponseSchema>

export const documentFileCommentMetadataSchema = z.object({
  $type: z.literal('DocumentFileComment'),
  SenderName: z.string(),
  ApplicationId: z.number(),
  DocumentFileId: z.number(),
  DocumentId: z.number(),
})

export type TDocumentFileCommentMetadata = z.infer<typeof documentFileCommentMetadataSchema>

export const applicationMessageMetadataSchema = z.object({
  $type: z.literal('ApplicationMessage'),
  SenderName: z.string(),
  ApplicationId: z.number(),
  MessageId: z.number(),
  MessagePreview: z.string(),
})

export type TApplicationMessageMetadata = z.infer<typeof applicationMessageMetadataSchema>

export const documentFileRejectedMetadataSchema = z.object({
  $type: z.literal('DocumentFileRejected'),
  ApplicationId: z.number(),
  DocumentFileId: z.number(),
  DocumentId: z.number(),
  FileName: z.string(),
})

export type TDocumentFileRejectedMetadata = z.infer<typeof documentFileRejectedMetadataSchema>

export const documentFileApprovedMetadataSchema = z.object({
  $type: z.literal('DocumentFileApproved'),
  ApplicationId: z.number(),
  DocumentFileId: z.number(),
  DocumentId: z.number(),
  FileName: z.string(),
})

export type TDocumentFileApprovedMetadata = z.infer<typeof documentFileApprovedMetadataSchema>

export const documentSubmittedMetadataSchema = z.object({
  $type: z.literal('DocumentSubmitted'),
  ApplicationId: z.number(),
  DocumentId: z.number().nullable().optional(),
  DocumentTypeId: z.number().nullable().optional(),
  ClientName: z.string().nullable().optional(),
})

export type TDocumentSubmittedMetadata = z.infer<typeof documentSubmittedMetadataSchema>

export const documentReviewCompletedMetadataSchema = z.object({
  $type: z.literal('DocumentReviewCompleted').optional(),
  ApplicationId: z.number(),
  ApprovedCount: z.number().nullable().optional(),
  RejectedCount: z.number().nullable().optional(),
  ApprovedDocumentNames: z.array(z.string()).nullable().optional(),
  RejectedDocumentNames: z.array(z.string()).nullable().optional(),
})

export type TDocumentReviewCompletedMetadata = z.infer<typeof documentReviewCompletedMetadataSchema>

export const staffDocumentUploadedMetadataSchema = z.object({
  $type: z.literal('StaffDocumentUploaded').optional(),
  ApplicationId: z.number(),
  DocumentName: z.string().nullable().optional(),
  DocumentId: z.number().nullable().optional(),
  DocumentFileId: z.number().nullable().optional(),
})

export type TStaffDocumentUploadedMetadata = z.infer<typeof staffDocumentUploadedMetadataSchema>

export const clientSubmitSignedFileMetadataSchema = z.object({
  $type: z.literal('ClientSubmitSignedFile'),
  ApplicationId: z.number(),
  ClientName: z.string(),
  DocumentName: z.string(),
  DocumentFileId: z.number(),
  ApplicationDocumentId: z.number().nullable().optional(),
  FileName: z.string(),
})

export type TClientSubmitSignedFileMetadata = z.infer<typeof clientSubmitSignedFileMetadataSchema>

export const applicationUnreadMessageReminderMetadataSchema = z.object({
  $type: z.literal('ApplicationUnreadMessageReminder').optional(),
  ApplicationId: z.number(),
  OldestUnreadAt: z.string().nullable().optional(),
})

export type TApplicationUnreadMessageReminderMetadata = z.infer<typeof applicationUnreadMessageReminderMetadataSchema>

export type TNotificationMetadata =
  | TDocumentFileCommentMetadata
  | TApplicationMessageMetadata
  | TDocumentFileRejectedMetadata
  | TDocumentFileApprovedMetadata
  | TDocumentSubmittedMetadata
  | TDocumentReviewCompletedMetadata
  | TStaffDocumentUploadedMetadata
  | TClientSubmitSignedFileMetadata
  | TApplicationUnreadMessageReminderMetadata

export function parseNotificationMetadata(metadataJson: string): TNotificationMetadata | null {
  try {
    const parsed = JSON.parse(metadataJson)

    if (parsed.$type === 'DocumentFileComment') {
      const result = documentFileCommentMetadataSchema.safeParse(parsed)
      if (result.success) {
        return result.data
      }
    }

    if (parsed.$type === 'ApplicationMessage') {
      const result = applicationMessageMetadataSchema.safeParse(parsed)
      if (result.success) {
        return result.data
      }
    }

    if (parsed.$type === 'DocumentFileRejected') {
      const result = documentFileRejectedMetadataSchema.safeParse(parsed)
      if (result.success) {
        return result.data
      }
    }

    if (parsed.$type === 'DocumentFileApproved') {
      const result = documentFileApprovedMetadataSchema.safeParse(parsed)
      if (result.success) {
        return result.data
      }
    }

    if (parsed.$type === 'DocumentSubmitted') {
      const result = documentSubmittedMetadataSchema.safeParse(parsed)
      if (result.success) {
        return result.data
      }
    }

    if (parsed.$type === 'DocumentReviewCompleted') {
      const result = documentReviewCompletedMetadataSchema.safeParse(parsed)
      if (result.success) {
        return result.data
      }
    }

    if (parsed.$type === 'ClientSubmitSignedFile') {
      const result = clientSubmitSignedFileMetadataSchema.safeParse(parsed)
      if (result.success) {
        return result.data
      }
    }

    if (parsed.$type === 'ApplicationUnreadMessageReminder') {
      const result = applicationUnreadMessageReminderMetadataSchema.safeParse(parsed)
      if (result.success) {
        return result.data
      }
    }

    if (parsed.$type === 'StaffDocumentUploaded' || (typeof parsed.ApplicationId === 'number' && 'DocumentName' in parsed)) {
      const result = staffDocumentUploadedMetadataSchema.safeParse(parsed)
      if (result.success) {
        return result.data
      }
    }

    return null
  } catch {
    return null
  }
}
